import { type DocumentPointReference, DocumentTemplateDataSourceApiKey } from 'primary-schema';

import type { BaseItem, NullablePartial } from '../common.js';

export enum DataSource {
  Crossmark = 'CROSSMARK',
  DateSigned = 'DATE_SIGNED',
  EhrScheduled = 'EHR_SCHEDULED',
  EmergencyContactName = 'EMERGENCY_CONTACT_NAME',
  EmergencyContactRelationship = 'EMERGENCY_CONTACT_RELATIONSHIP',
  EmergencyContactPhone = 'EMERGENCY_CONTACT_PHONE',
  GuardianRelationship = 'GUARDIAN_RELATIONSHIP',
  MRN = 'MRN', // deprecated use PATIENT_MRN
  None = 'NONE',
  Signature = 'SIGNATURE',

  GuarantorAge = 'GUARANTOR_AGE',
  GuarantorDOB = 'GUARANTOR_DOB',
  GuarantorHomeAddress = 'GUARANTOR_HOME_ADDRESS',
  GuarantorHomeFullAddress = 'GUARANTOR_HOME_FULL_ADDRESS',
  GuarantorHomeCityStateZip = 'GUARANTOR_HOME_CITY_STATE_ZIP',
  GuarantorHomePhoneNumber = 'GUARANTOR_HOME_PHONE_NUMBER',
  GuarantorName = 'GUARANTOR_NAME',
  GuarantorRelationship = 'GUARANTOR_RELATIONSHIP', // deprecated use GUARANTOR_RELATIONSHIP_TO_PATIENT
  GuarantorRelationshipToPatient = 'GUARANTOR_RELATIONSHIP_TO_PATIENT',
  GuarantorSex = 'GUARANTOR_SEX',
  GuarantorSSN = 'GUARANTOR_SSN',

  PatientAge = 'PATIENT_AGE',
  NewbornAge = 'NEWBORN_AGE',
  PatientBirthGestationalAge = 'PATIENT_BIRTH_GESTATIONAL_AGE',
  PatientBirthWeightGrams = 'PATIENT_BIRTH_WEIGHT_GRAMS',
  PatientDOB = 'PATIENT_DOB',
  PatientEmail = 'PATIENT_EMAIL',
  PatientEmployer = 'PATIENT_EMPLOYER',
  PatientPreferredLanguage = 'PATIENT_PREFERRED_LANGUAGE',
  PatientCareProviderAddress = 'PATIENT_CARE_PROVIDER_ADDRESS',
  PatientCareProviderName = 'PATIENT_CARE_PROVIDER_NAME',
  PatientHomeAddress = 'PATIENT_HOME_ADDRESS',
  PatientHomeFullAddress = 'PATIENT_HOME_FULL_ADDRESS',
  PatientHomeCityStateZip = 'PATIENT_HOME_CITY_STATE_ZIP',
  PatientHomePhoneNumber = 'PATIENT_HOME_PHONE_NUMBER',
  PatientInitials = 'PATIENT_INITIALS',
  PatientIP = 'PATIENT_IP',
  PatientMobilePhoneNumber = 'PATIENT_MOBILE_PHONE_NUMBER',
  PatientMotherName = 'PATIENT_MOTHER_NAME',
  PatientMRN = 'PATIENT_MRN',
  PatientPharmacyNameWithAddress = 'PATIENT_PHARMACY_NAME_WITH_ADDRESS',
  PatientPharmacyPhone = 'PATIENT_PHARMACY_PHONE',
  PatientAthenaIdxMrn = 'PATIENT_ATHENA_IDX_MRN',
  PatientName = 'PATIENT_NAME',
  PatientRace = 'PATIENT_RACE',
  PatientSex = 'PATIENT_SEX',
  PatientSSN = 'PATIENT_SSN',

  PrimaryInsuranceGroupNumber = 'PRIMARY_INSURANCE_GROUP_NUMBER',
  PrimaryInsuranceInsuranceName = 'PRIMARY_INSURANCE_INSURANCE_NAME',
  PrimaryInsurancePolicyHolderAge = 'PRIMARY_INSURANCE_POLICY_HOLDER_AGE',
  PrimaryInsurancePolicyHolderDOB = 'PRIMARY_INSURANCE_POLICY_HOLDER_DOB',
  PrimaryInsurancePolicyHolderHomeAddress = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  PrimaryInsurancePolicyHolderHomeCityStateZip = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  PrimaryInsurancePolicyHolderHomePhoneNumber = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  PrimaryInsurancePolicyHolderName = 'PRIMARY_INSURANCE_POLICY_HOLDER_NAME',
  PrimaryInsurancePolicyHolderRelationshipToPatient = 'PRIMARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  PrimaryInsurancePolicyHolderSex = 'PRIMARY_INSURANCE_POLICY_HOLDER_SEX',
  PrimaryInsurancePolicyNumber = 'PRIMARY_INSURANCE_POLICY_NUMBER',

  SecondaryInsuranceGroupNumber = 'SECONDARY_INSURANCE_GROUP_NUMBER',
  SecondaryInsuranceInsuranceName = 'SECONDARY_INSURANCE_INSURANCE_NAME',
  SecondaryInsurancePolicyHolderAge = 'SECONDARY_INSURANCE_POLICY_HOLDER_AGE',
  SecondaryInsurancePolicyHolderDOB = 'SECONDARY_INSURANCE_POLICY_HOLDER_DOB',
  SecondaryInsurancePolicyHolderHomeAddress = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  SecondaryInsurancePolicyHolderHomeCityStateZip = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  SecondaryInsurancePolicyHolderHomePhoneNumber = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  SecondaryInsurancePolicyHolderName = 'SECONDARY_INSURANCE_POLICY_HOLDER_NAME',
  SecondaryInsurancePolicyHolderRelationshipToPatient = 'SECONDARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  SecondaryInsurancePolicyHolderSex = 'SECONDARY_INSURANCE_POLICY_HOLDER_SEX',
  SecondaryInsurancePolicyNumber = 'SECONDARY_INSURANCE_POLICY_NUMBER',

  VisitAdmitDate = 'VISIT_ADMIT_DATE',
  VisitAdmitDatetime = 'VISIT_ADMIT_DATETIME',
  VisitAdmittingPhysicianName = 'VISIT_ADMITTING_PHYSICIAN_NAME',
  VisitAttendingPhysicianName = 'VISIT_ATTENDING_PHYSICIAN_NAME',
  VisitBuildingLocation = 'VISIT_BUILDING_LOCATION',
  VisitEHRID = 'VISIT_EHR_ID',
  VisitEHRType = 'VISIT_EHR_TYPE',
  VisitMedicalService = 'VISIT_MEDICAL_SERVICE',
  VisitReason = 'VISIT_REASON',
}

export const DataSourceToDataSourceApiKeys: Record<DataSource, DocumentTemplateDataSourceApiKey> = {
  [DataSource.Crossmark]: DocumentTemplateDataSourceApiKey.Crossmark,
  [DataSource.DateSigned]: DocumentTemplateDataSourceApiKey.DateSigned,
  [DataSource.EhrScheduled]: DocumentTemplateDataSourceApiKey.EhrScheduled,
  [DataSource.EmergencyContactName]: DocumentTemplateDataSourceApiKey.EmergencyContactName,
  [DataSource.EmergencyContactPhone]: DocumentTemplateDataSourceApiKey.EmergencyContactPhone,
  [DataSource.EmergencyContactRelationship]:
    DocumentTemplateDataSourceApiKey.EmergencyContactRelationship,
  [DataSource.GuarantorAge]: DocumentTemplateDataSourceApiKey.GuarantorAge,
  [DataSource.GuarantorDOB]: DocumentTemplateDataSourceApiKey.GuarantorDob,
  [DataSource.GuarantorHomeAddress]: DocumentTemplateDataSourceApiKey.GuarantorHomeAddress,
  [DataSource.GuarantorHomeCityStateZip]:
    DocumentTemplateDataSourceApiKey.GuarantorHomeCityStateZip,
  [DataSource.GuarantorHomeFullAddress]: DocumentTemplateDataSourceApiKey.GuarantorHomeFullAddress,
  [DataSource.GuarantorHomePhoneNumber]: DocumentTemplateDataSourceApiKey.GuarantorHomePhoneNumber,
  [DataSource.GuarantorName]: DocumentTemplateDataSourceApiKey.GuarantorName,
  [DataSource.GuarantorRelationship]: DocumentTemplateDataSourceApiKey.GuarantorRelationship,
  [DataSource.GuarantorRelationshipToPatient]:
    DocumentTemplateDataSourceApiKey.GuarantorRelationshipToPatient,
  [DataSource.GuarantorSex]: DocumentTemplateDataSourceApiKey.GuarantorSex,
  [DataSource.GuarantorSSN]: DocumentTemplateDataSourceApiKey.GuarantorSsn,
  [DataSource.GuardianRelationship]: DocumentTemplateDataSourceApiKey.GuardianRelationship,
  [DataSource.MRN]: DocumentTemplateDataSourceApiKey.Mrn,
  [DataSource.NewbornAge]: DocumentTemplateDataSourceApiKey.NewbornAge,
  [DataSource.None]: DocumentTemplateDataSourceApiKey.None,
  [DataSource.PatientAge]: DocumentTemplateDataSourceApiKey.PatientAge,
  [DataSource.PatientAthenaIdxMrn]: DocumentTemplateDataSourceApiKey.PatientAthenaIdxMrn,
  [DataSource.PatientBirthGestationalAge]:
    DocumentTemplateDataSourceApiKey.PatientBirthGestationalAge,
  [DataSource.PatientBirthWeightGrams]: DocumentTemplateDataSourceApiKey.PatientBirthWeightGrams,
  [DataSource.PatientCareProviderAddress]:
    DocumentTemplateDataSourceApiKey.PatientCareProviderAddress,
  [DataSource.PatientCareProviderName]: DocumentTemplateDataSourceApiKey.PatientCareProviderName,
  [DataSource.PatientDOB]: DocumentTemplateDataSourceApiKey.PatientDob,
  [DataSource.PatientEmail]: DocumentTemplateDataSourceApiKey.PatientEmail,
  [DataSource.PatientEmployer]: DocumentTemplateDataSourceApiKey.PatientEmployer,
  [DataSource.PatientHomeAddress]: DocumentTemplateDataSourceApiKey.PatientHomeAddress,
  [DataSource.PatientHomeCityStateZip]: DocumentTemplateDataSourceApiKey.PatientHomeCityStateZip,
  [DataSource.PatientHomeFullAddress]: DocumentTemplateDataSourceApiKey.PatientHomeFullAddress,
  [DataSource.PatientHomePhoneNumber]: DocumentTemplateDataSourceApiKey.PatientHomePhoneNumber,
  [DataSource.PatientInitials]: DocumentTemplateDataSourceApiKey.PatientInitials,
  [DataSource.PatientIP]: DocumentTemplateDataSourceApiKey.PatientIp,
  [DataSource.PatientMobilePhoneNumber]: DocumentTemplateDataSourceApiKey.PatientMobilePhoneNumber,
  [DataSource.PatientMotherName]: DocumentTemplateDataSourceApiKey.PatientMotherName,
  [DataSource.PatientMRN]: DocumentTemplateDataSourceApiKey.PatientMrn,
  [DataSource.PatientName]: DocumentTemplateDataSourceApiKey.PatientName,
  [DataSource.PatientPharmacyNameWithAddress]:
    DocumentTemplateDataSourceApiKey.PatientPharmacyNameWithAddress,
  [DataSource.PatientPharmacyPhone]: DocumentTemplateDataSourceApiKey.PatientPharmacyPhone,
  [DataSource.PatientPreferredLanguage]: DocumentTemplateDataSourceApiKey.PatientPreferredLanguage,
  [DataSource.PatientRace]: DocumentTemplateDataSourceApiKey.PatientRace,
  [DataSource.PatientSex]: DocumentTemplateDataSourceApiKey.PatientSex,
  [DataSource.PatientSSN]: DocumentTemplateDataSourceApiKey.PatientSsn,
  [DataSource.PrimaryInsuranceGroupNumber]:
    DocumentTemplateDataSourceApiKey.PrimaryInsuranceGroupNumber,
  [DataSource.PrimaryInsuranceInsuranceName]:
    DocumentTemplateDataSourceApiKey.PrimaryInsuranceInsuranceName,
  [DataSource.PrimaryInsurancePolicyHolderAge]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderAge,
  [DataSource.PrimaryInsurancePolicyHolderDOB]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderDOB,
  [DataSource.PrimaryInsurancePolicyHolderHomeAddress]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderHomeAddress,
  [DataSource.PrimaryInsurancePolicyHolderHomeCityStateZip]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderHomeCityStateZip,
  [DataSource.PrimaryInsurancePolicyHolderHomePhoneNumber]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderHomePhoneNumber,
  [DataSource.PrimaryInsurancePolicyHolderName]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderName,
  [DataSource.PrimaryInsurancePolicyHolderRelationshipToPatient]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderRelationshipToPatient,
  [DataSource.PrimaryInsurancePolicyHolderSex]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyHolderSex,
  [DataSource.PrimaryInsurancePolicyNumber]:
    DocumentTemplateDataSourceApiKey.PrimaryInsurancePolicyNumber,
  [DataSource.SecondaryInsuranceGroupNumber]:
    DocumentTemplateDataSourceApiKey.SecondaryInsuranceGroupNumber,
  [DataSource.SecondaryInsuranceInsuranceName]:
    DocumentTemplateDataSourceApiKey.SecondaryInsuranceInsuranceName,
  [DataSource.SecondaryInsurancePolicyHolderAge]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderAge,
  [DataSource.SecondaryInsurancePolicyHolderDOB]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderDOB,
  [DataSource.SecondaryInsurancePolicyHolderHomeAddress]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderHomeAddress,
  [DataSource.SecondaryInsurancePolicyHolderHomeCityStateZip]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderHomeCityStateZip,
  [DataSource.SecondaryInsurancePolicyHolderHomePhoneNumber]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderHomePhoneNumber,
  [DataSource.SecondaryInsurancePolicyHolderName]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderName,
  [DataSource.SecondaryInsurancePolicyHolderRelationshipToPatient]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderRelationshipToPatient,
  [DataSource.SecondaryInsurancePolicyHolderSex]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyHolderSex,
  [DataSource.SecondaryInsurancePolicyNumber]:
    DocumentTemplateDataSourceApiKey.SecondaryInsurancePolicyNumber,
  [DataSource.Signature]: DocumentTemplateDataSourceApiKey.Signature,
  [DataSource.VisitAdmitDate]: DocumentTemplateDataSourceApiKey.VisitAdmitDate,
  [DataSource.VisitAdmitDatetime]: DocumentTemplateDataSourceApiKey.VisitAdmitDatetime,
  [DataSource.VisitAdmittingPhysicianName]:
    DocumentTemplateDataSourceApiKey.VisitAdmittingPhysicianName,
  [DataSource.VisitAttendingPhysicianName]:
    DocumentTemplateDataSourceApiKey.VisitAttendingPhysicianName,
  [DataSource.VisitBuildingLocation]: DocumentTemplateDataSourceApiKey.VisitBuildingLocation,
  [DataSource.VisitEHRID]: DocumentTemplateDataSourceApiKey.VisitEhrId,
  [DataSource.VisitEHRType]: DocumentTemplateDataSourceApiKey.VisitEhrType,
  [DataSource.VisitMedicalService]: DocumentTemplateDataSourceApiKey.VisitMedicalService,
  [DataSource.VisitReason]: DocumentTemplateDataSourceApiKey.VisitReason,
};

export enum Font {
  Helvetica = 'Helvetica',
  SignPainterHouseScript = 'SignPainterHouseScript',
  UbuntuR = 'Ubuntu R',
}

export enum SigningFor {
  Myself = 'myself',
  Guardian = 'guardian',
}

export interface Command {
  dataSource: string;
  font: Font;
  fontSize: number;
  fontColorR: number; // 0-255
  fontColorG: number; // 0-255
  fontColorB: number; // 0-255
  pageIndex: number;
  // 0, 0 is bottom left of the PDF page
  pointX: number;
  pointY: number;
  pointRef: DocumentPointReference;
  lineWidth?: number;
  mappedValue?: DataSource.Crossmark | DataSource.None;
  previsitFormFieldId?: string;
  value?: string;
}

export enum Category {
  ConditionsOfAdmission = 'CONDITIONS_OF_ADMISSION',
  EncounterDocumentPatientHistory = 'ENCOUNTER_DOCUMENT_PATIENT_HISTORY',
  FinancialPolicy = 'FINANCIAL_POLICY',
  FormSummary = 'FORM_SUMMARY',
  HealthInformationExchange = 'HEALTH_INFORMATION_EXCHANGE',
  HealthInformationExchangeAcknowledgment = 'HEALTH_INFORMATION_EXCHANGE_ACKNOWLEDGMENT',
  MedicationPolicy = 'MEDICATION_POLICY',
  NoticeOfAdmission = 'NOTICE_OF_ADMISSION',
  PatientAuthorization = 'PATIENT_AUTHORIZATION',
  PatientChargeNotice = 'PATIENT_CHARGE_NOTICE',
  PatientConsent = 'PATIENT_CONSENT',
  PatientConsentBehavioralHealth = 'PATIENT_CONSENT_BEHAVIORAL_HEALTH',
  PatientConsentEstablishedPatient = 'PATIENT_CONSENT_ESTABLISHED_PATIENT',
  PatientConsentNewPatient = 'PATIENT_CONSENT_NEW_PATIENT',
  PatientConsentOneConsent = 'PATIENT_CONSENT_ONE_CONSENT',
  PatientConsentRemoteMonitoring = 'PATIENT_CONSENT_REMOTE_MONITORING',
  PatientFeeDisclosure = 'PATIENT_FEE_DISCLOSURE',
  PatientRelease = 'PATIENT_RELEASE',
  PatientSpecialtyConsent = 'PATIENT_SPECIALTY_CONSENT',
  PatientTranscriptionConsent = 'PATIENT_TRANSCRIPTION_CONSENT',
  PrivacyPolicy = 'PRIVACY_POLICY',
  TreatmentConsent = 'TREATMENT_CONSENT',
  VirtualConsent = 'VIRTUAL_CONSENT',
}

export interface BaseDocumentTemplate {
  name: string;
  category: Category;
  commands: Command[];
  gsPath: string;
  fullReviewRequired: boolean;
  viewRequired: boolean;
  deleted: Date | null;
  organizationId: string | null;
  internalName: string | null;
}

export interface DocumentTemplate extends BaseItem, BaseDocumentTemplate {}

export type CreateValues = NullablePartial<
  Omit<BaseDocumentTemplate, 'fullReviewRequired' | 'viewRequired'>
> & {
  fullReviewRequired?: boolean;
  viewRequired?: boolean;
};
export type UpdateValues = Partial<BaseDocumentTemplate>;

export interface FindAllOptions extends Partial<
  Pick<DocumentTemplate, 'name' | 'category' | 'organizationId' | 'internalName'>
> {
  q?: string;
  includeDeleted?: boolean;
}
